/**
 * User Management Page
 * Main page with tabs for Users, Roles, Permissions, Hierarchy
 */

import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tabs, Tab, Tooltip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { invalidateUserManagementCache } from '@/hooks/useUserManagement';
import Users from './Users';
import Roles from './Roles';
import Hierarchy from './Hierarchy';
import Reports from './Reports';
import UsageChip from '@/components/common/UsageChip';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-management-tabpanel-${index}`}
      aria-labelledby={`user-management-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const UserManagement: React.FC = () => {
  const [value, setValue] = useState(0);
  // Tabs read through a shared cache (useUserManagement), so switching tabs no
  // longer refetches. Refresh drops the cache and remounts the visible tab,
  // which re-reads everything it shows — for changes made by another admin.
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => {
    invalidateUserManagementCache();
    setRefreshKey((k) => k + 1);
  };

  // Handle hash navigation
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#users') setValue(0);
    else if (hash === '#roles') setValue(1);
    else if (hash === '#hierarchy') setValue(2);
    else if (hash === '#reports') setValue(3);
  }, []);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const hashMap: string[] = ['#users', '#roles', '#hierarchy', '#reports'];
    const hash = hashMap[newValue] || '#users';
    window.location.hash = hash;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <UsageChip key={`users-${refreshKey}`} resource="users" label="Users" />
          <UsageChip key={`roles-${refreshKey}`} resource="roles" label="Roles" />
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} aria-label="Refresh user management data">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={value} onChange={handleChange}>
          <Tab label="Users" />
          <Tab label="Roles" />
          <Tab label="Hierarchy" />
          <Tab label="Reports" />
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        <Users key={refreshKey} />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Roles key={refreshKey} />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <Hierarchy key={refreshKey} />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <Reports key={refreshKey} />
      </TabPanel>
    </Box>
  );
};

export default UserManagement;
